import { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE } from "utils";

function AktivasiBukuDinas() {
  const [dinasId, setDinasId] = useState(null);
  const [bukuDinas, setBukuDinas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [userStatus, setUserStatus] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      setUserStatus(user.status);
      fetchBukuDinas(user.dinas_id);
    } else {
      logoutUser(navigate);
    }
  }, []);

  const fetchBukuDinas = async (idDinas) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}buku-tamu/buku-dinas/${idDinas}`
      );
      setBukuDinas(response.data);
    } catch (error) {
      if (error.response?.status === 404 || !error.response?.data) {
        setBukuDinas(null); // Belum ada buku dinas
      } else {
        console.error("Error fetching buku dinas:", error);
        swal({
          title: "Error",
          text: "Gagal mengambil data buku dinas",
          icon: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAktivasi = async () => {
    if (!dinasId) return;

    setActivating(true);
    try {
      const response = await axios.post(
        `${API_ESA_OFFICE}buku-tamu/buku-dinas`,
        {
          id_dinas: dinasId,
        }
      );

      swal({
        title: "Berhasil!",
        text: "Buku dinas berhasil diaktifkan",
        icon: "success",
        button: "OK",
      });

      setBukuDinas(response.data);

      // Trigger custom event untuk update badge di menu
      window.dispatchEvent(
        new CustomEvent("bukuDinasUpdated", { detail: response.data })
      );
    } catch (error) {
      console.error("Error activating buku dinas:", error);
      swal({
        title: "Error",
        text: error.response?.data?.message || "Gagal mengaktifkan buku dinas",
        icon: "error",
        button: "OK",
      });
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
            <MDTypography variant="body2" color="text" ml={2}>
              Memuat data...
            </MDTypography>
          </MDBox>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor={bukuDinas ? "success" : "warning"}
                borderRadius="lg"
                coloredShadow={bukuDinas ? "success" : "warning"}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Aktivasi Buku Tamu Dinas
                </MDTypography>
                {bukuDinas ? (
                  <MDBox
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      height: "18px",
                      padding: "0 6px",
                      borderRadius: "9px",
                      fontSize: "0.65rem",
                      fontWeight: 500,
                      backgroundColor: "#4caf50",
                      color: "#ffffff",
                      whiteSpace: "nowrap",
                      lineHeight: 1,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: "14px" }} />
                    Aktif
                  </MDBox>
                ) : (
                  <MDBox
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      height: "18px",
                      padding: "0 6px",
                      borderRadius: "9px",
                      fontSize: "0.65rem",
                      fontWeight: 500,
                      backgroundColor: "#ff9800",
                      color: "#ffffff",
                      whiteSpace: "nowrap",
                      lineHeight: 1,
                    }}
                  >
                    <CancelIcon sx={{ fontSize: "14px" }} />
                    Belum Aktif
                  </MDBox>
                )}
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {bukuDinas ? (
                  <MDBox>
                    <MDTypography variant="h6" gutterBottom>
                      Buku Tamu Dinas Sudah Aktif
                    </MDTypography>
                    <MDBox mt={3}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            Link Buku Tamu:
                          </MDTypography>
                          <MDBox mt={1}>
                            <MDTypography
                              variant="body2"
                              color="info"
                              sx={{ wordBreak: "break-all" }}
                            >
                              {`https://esa-office.rotendaokab.go.id/#/publik/buku-tamu/${bukuDinas.link}`}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            QR Code:
                          </MDTypography>
                          <MDBox mt={1}>
                            {bukuDinas.qr_code && (
                              <img
                                src={`https://esa-office.rotendaokab.go.id${
                                  bukuDinas.qr_code.startsWith("/")
                                    ? bukuDinas.qr_code
                                    : "/" + bukuDinas.qr_code
                                }`}
                                alt="QR Code"
                                style={{
                                  width: 150,
                                  height: 150,
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  const qrPath = bukuDinas.qr_code.startsWith(
                                    "/"
                                  )
                                    ? bukuDinas.qr_code
                                    : "/" + bukuDinas.qr_code;
                                  window.open(
                                    `https://esa-office.rotendaokab.go.id${qrPath}`,
                                    "_blank"
                                  );
                                }}
                              />
                            )}
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </MDBox>
                ) : (
                  <MDBox>
                    <MDTypography variant="h6" gutterBottom>
                      Aktifkan Buku Tamu Dinas
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary" mb={3}>
                      Klik tombol di bawah ini untuk mengaktifkan buku tamu
                      dinas. Setelah diaktifkan, link dan QR Code akan otomatis
                      dibuat.
                    </MDTypography>
                    <Button
                      variant="contained"
                      color="info"
                      onClick={handleAktivasi}
                      disabled={
                        activating ||
                        (userStatus !== "admin" && userStatus !== "super admin")
                      }
                      startIcon={
                        activating ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : null
                      }
                    >
                      {activating ? "Mengaktifkan..." : "Aktifkan Buku Tamu"}
                    </Button>
                    {userStatus !== "admin" && userStatus !== "super admin" && (
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        mt={1}
                        display="block"
                      >
                        Hanya admin atau super admin yang dapat mengaktifkan
                        buku tamu dinas.
                      </MDTypography>
                    )}
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AktivasiBukuDinas;
